import { ControlledDateOnlyPicker, ControlledTextField } from '@if/mui'
import CloseIcon from '@mui/icons-material/Close'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import { Avatar, Box, Button, Divider, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from '@mui/material'
import { useGetPersonById, useUpdatePersonById } from '@src/api/person-api'
import PageContainer from '@src/components/Layout/PageContainer'
import PageHeader from '@src/components/Layout/PageHeader'
import PageLoader from '@src/components/Loader/PageLoader'
import { Person, VALID_TEAMS } from '@src/types/Person'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { usePersonResolver } from './Person-schema'

interface Props {
  id: string
}

const PersonDetailsEditPage = ({ id }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [data, isFetching] = useGetPersonById(id)
  const [updatePerson, isUpdating] = useUpdatePersonById()

  const resolver = usePersonResolver()
  const { control, handleSubmit } = useForm({
    resolver,
    defaultValues: {
      email: data ? data.email : '',
      fullName: data ? data.fullName : '',
      startDate: data ? data.startDate : '',
      endDate: data ? data.endDate : '',
      bambooId: data ? data.bambooId : '',
      team: data?.team ?? 'Dev',
    },
  })

  const onSave = handleSubmit((data) => {
    const person: Person = {
      id: id,
      email: data.email,
      fullName: data.fullName,
      startDate: data.startDate,
      endDate: data.endDate,
      bambooId: data.bambooId,
      team: data.team,
    }

    void updatePerson(person).then(() => {
      navigate('/people/' + id)
    })
  })

  if (isFetching || !data) return <PageLoader />

  return (
    <>
      <PageHeader
        title={t('menus.personDetails')}
        breadcrumbs={{
          path: [
            { href: '/people', label: t('breadcrumbs.people') },
            { href: '/people/' + id, label: data.fullName },
            { href: '/people/' + id + '/edit', label: t('common.edit') },
          ],
        }}
      />
      <PageContainer>
        <form onSubmit={onSave}>
          <Stack direction="row" sx={{ width: '100%', height: '100%' }} spacing={1} marginTop={1}>
            <Stack
              sx={{
                padding: 0,
                alignItems: 'center',
                width: '20%',
              }}
            >
              <Paper
                sx={{
                  padding: 1,
                  width: '100%',
                }}
                elevation={3}
              >
                <Box display="flex" alignItems="center" justifyContent="center" margin={1}>
                  <Avatar
                    alt={data.fullName}
                    sx={{
                      width: 168,
                      height: 168,
                    }}
                  >
                    {data.fullName[0]}
                  </Avatar>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center" margin={1}>
                  <ControlledTextField control={control} name="fullName" label={t('person.fullName')} />
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center" margin={1}>
                  <Typography> Develloper</Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="flex-start" padding={1} flexDirection="column" margin={1}>
                  <Typography variant="h6">Status</Typography>
                  <Typography>{data.endDate == null ? 'Actif' : 'Non Actif'}</Typography>
                </Box>
                <Box display="flex" justifyContent="flex-start" padding={1} flexDirection="column" margin={1}>
                  <ControlledTextField control={control} label={t('person.bambooId')} name="bambooId" />
                </Box>
                <Box display="flex" justifyContent="flex-start" padding={1} flexDirection="column" margin={1}>
                  <FormControl fullWidth required>
                    <InputLabel id="team-select-label">{t('person.team')}</InputLabel>
                    <Controller
                      name="team"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} labelId="team-select-label" label={t('person.team')} required>
                          {VALID_TEAMS.map((team) => (
                            <MenuItem key={team} value={team}>
                              {team}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                </Box>
              </Paper>
            </Stack>

            <Stack
              direction="row"
              sx={{
                padding: 0,
                width: '80%',
              }}
            >
              <Paper
                sx={{
                  width: '100%',
                }}
                elevation={3}
              >
                <Box display="flex" justifyContent="space-between" padding={1}>
                  <Typography variant="h4">Basic Information</Typography>
                  <Stack direction="row-reverse" sx={{ paddingBottom: 1 }} spacing={1}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      startIcon={<SaveAltIcon />}
                      disabled={isUpdating}
                    >
                      {t('common.save')}
                    </Button>
                    <Button
                      variant="contained"
                      color="inherit"
                      onClick={() => {
                        navigate('/people/' + id)
                      }}
                      startIcon={<CloseIcon />}
                      disabled={isUpdating}
                    >
                      {t('common.cancel')}
                    </Button>
                  </Stack>
                </Box>
                <Box display="flex" justifyContent="flex-start" padding={1} flexDirection="column">
                  <ControlledDateOnlyPicker
                    control={control}
                    name="startDate"
                    label={t('person.startDate')}
                    sx={{ width: '50%' }}
                  />
                </Box>
                <Box display="flex" justifyContent="flex-start" padding={1} flexDirection="column">
                  <ControlledDateOnlyPicker
                    control={control}
                    label={t('person.endDate')}
                    name="endDate"
                    sx={{ width: '50%' }}
                  />
                </Box>
                <Box display="flex" justifyContent="flex-start" padding={1} flexDirection="column">
                  <ControlledTextField control={control} name="email" label={t('person.email')} sx={{ width: '50%' }} />
                </Box>
              </Paper>
            </Stack>
          </Stack>
        </form>
      </PageContainer>
    </>
  )
}

export default PersonDetailsEditPage
